import Box from '@mui/material/Box';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import * as yup from 'yup';

import { FormFieldInput } from '@/components/molecules/FormFieldInput';
import { Field } from '@/types/fields';
import { toEmptyStringIfNull } from '@/utility/toEmptyStringIfNull';

interface Props<T> {
  readonly fields: ReadonlyArray<Field<T>>;
  readonly defaultValue?: Record<string, unknown>;
  readonly errors?: Record<string, yup.ValidationError>;
  readonly columnsCount?: number;
}

export function NestedFields<T>(props: Props<T>): React.ReactElement {
  const { fields, defaultValue, errors, columnsCount } = props;

  const containerStyle = React.useMemo(
    (): SystemStyleObject => ({
      display: 'grid',
      gridTemplateColumns: `repeat(${columnsCount}, 1fr)`,
      gap: 2,
    }),
    [columnsCount],
  );

  return (
    <Box sx={containerStyle}>
      {fields.map((field: Field<T>): React.ReactElement => {
        const sx = {
          gridColumn: `auto / span ${field.columnSpan ?? 1}`,
          minWidth: 0,
        };
        const key = String(field.name);
        const childField: Field<T> = { ...field, name: key };
        const error = field.displayErrors ? errors?.[key] : undefined;
        const fieldValue = toEmptyStringIfNull(
          (defaultValue as Record<string, unknown> | undefined)?.[String(field.name)],
        );

        return (
          <Box key={key} sx={sx}>
            <FormFieldInput field={childField} error={error} defaultValue={fieldValue} />
          </Box>
        );
      })}
    </Box>
  );
}
